import { ref, computed, inject, onBeforeMount, onUnmounted, nextTick, getCurrentInstance } from 'vue'
import type { Ref } from 'vue'
import { emitRtDialogClosedWithoutSubmit, emitRtChainAdvanceRequest } from '@/plugins/emitter'
import City from '@/plugins/tryt-city-master'
import { useRealtimeTransferSend, RtSendFactoryKey, type RtSendReturn } from '@/composables/useRealtimeTransferSend'
import { RT_SUB_ACCEPT_LABEL_AFTER_SEND } from '@/composables/useRtPopupRepeatChain'

/**
 * RT 系ダイアログ（RealtimeTransferCommon / RealtimeTransferCommonTest1 /
 *  Bizreach / Medley / MedleyLink）の「基本骨格」を提供する composable。
 *
 * 設計方針:
 *  - 5 ダイアログ共通の state / pdfFormData / open-close 骨格 / send-* 全取得 をここに集約。
 *  - 「のぞみメタファー」: 全 send-* 関数は base が rtSendFactory から取得して return するが、
 *    実際に emitter で listen するのは options.primaryConsultEventName と
 *    Common/Test1 が追加登録する RT 共通群のみ。誤発火事故を防ぐためダイアログを跨いだ
 *    強制ディスパッチはしない。
 *  - Bizreach の password / Common の YouTube・conditionId・mypage-rt-* 等は
 *    フック (beforeApplyInfo / afterApplyInfo / onBeforeCloseExtra / resetStateBeforeOpen) で
 *    各ダイアログ側に分離する。base 自身は汎用部分だけを担当。
 */

export interface UseRtDialogBaseOptions {
  /** mitt 経由でこのダイアログを開くイベント名（例: 'open-bizreach-dialog'） */
  openDialogEventName: string
  /**
   * このダイアログが「のぞみ役」として代表 listen する送客 hub イベント名。
   * 例: 'send-bizreach-consult' / 'send-realtimetransferappeal-consult' / 'send-realtimetransfercommon-consult'
   * 未指定の場合は send-* を一切 on しない（テンプレートから直接呼ぶ運用）。
   */
  primaryConsultEventName?: string
  /**
   * primaryConsultEventName に紐付ける send 関数のキー。
   * rtSendFactory の戻り値（RtSendReturn）から選ぶ。
   */
  primaryConsultHandlerKey?: keyof RtSendReturn

  /** 既定 permissionType（Medley=4, MedleyLink=5 等） */
  defaultPermissionType?: number
  /** 既定 clientId（Medley/MedleyLink: 'medley_link'） */
  defaultClientId?: string
  /** 既定 dialogName（Medley/MedleyLink: 'medley_link-dialog'） */
  defaultDialogName?: string

  /** Bizreach 用パスワード ref（rtSendFactory にそのまま渡す） */
  bizreachPassword?: Ref<string>
  bizreachPasswordFormRef?: Ref<any>

  /**
   * mitt argList で渡される「openDialog 公式引数(6個)以後の追加引数」を
   * ダイアログ固有の state に振り分ける用フック。
   * 例: Common の openRealtimeTransferCommonDialog(id, html, perm, info, bd, mypage, sizeUp)
   *      → extraArgs = [sizeUp]
   */
  applyExtraArgs?: (extraArgs: unknown[]) => void

  /**
   * info 適用前に走る非同期フック。conditionId フェッチ等の info 上書きに使う。
   * - return false: open 処理を中断
   * - return オブジェクト: perm / info を差し替え
   * - return undefined: そのまま続行
   */
  beforeApplyInfo?: (ctx: { perm: number; info: Record<string, any> }) =>
    Promise<false | void | { perm?: number; info?: Record<string, any> }>

  /** info 適用後フック（text 系上書き、YouTube ロード、bizreachForm.password = '' 等） */
  afterApplyInfo?: (ctx: { info: Record<string, any> }) => void

  /**
   * handleBeforeClose の最後に呼ばれる追加処理。
   * Common/Test1 の mypage-rt-* イベント emit 等に使う。
   */
  onBeforeCloseExtra?: (ctx: { isSended: boolean; isMypage: boolean }) => void

  /**
   * open 直前に呼ばれる state リセット用フック。
   * Common: text1/text2/agree_text/buttoneTxt の既定値復元、
   * Bizreach: bizreachForm.password = '' 等。
   */
  resetStateBeforeOpen?: () => void
}

export type RegisterConsultEmitter = (event: string, handler: (...args: unknown[]) => unknown) => void

export function useRtDialogBase (options: UseRtDialogBaseOptions) {
  const {
    openDialogEventName,
    primaryConsultEventName,
    primaryConsultHandlerKey,
    defaultPermissionType = 1,
    defaultClientId = '',
    defaultDialogName = '',
    bizreachPassword,
    bizreachPasswordFormRef,
    applyExtraArgs,
    beforeApplyInfo,
    afterApplyInfo,
    onBeforeCloseExtra,
    resetStateBeforeOpen
  } = options

  const vm = getCurrentInstance()!.proxy as any

  // ---- 共通 state ----
  const isMypage = ref(false)
  const formatid = ref('')
  const previewHtml = ref('')
  const permissionType = ref(defaultPermissionType)
  /**
   * 「送客完了済みフラグ」。
   * 旧名 isRealtimeTransferCommonSended / isBizreachSended / isSendedMedley を本基底で一元化。
   */
  const isSended = ref(false)
  const fullscreenLoading = ref(false)
  const username = ref('')
  const clientId = ref(defaultClientId)
  const dialogName = ref(defaultDialogName)
  const isAgree = ref(true)
  const basicData = ref<Record<string, any>>({})
  const dialogInfo = ref<Record<string, any>>({})
  const modalVisible = ref(false)
  const modalOpened = ref(false)
  const pendingReshow = ref(false)
  const replacingOpen = ref(false)

  // ---- 共通 computed ----
  const pdfFormData = computed(() => {
    const fd = new FormData()
    fd.append('app_html', previewHtml.value)
    fd.append('pdf_option', 'A4')
    return fd
  })

  // ---- 共通関数 ----
  function createFilename (ext: string): string {
    const now = new Date()
    const prefix = process.env.VUE_APP_DOWNLOAD_FILENAME_PREFIX ?? ''
    const dateString = vm.$formLib.dateFormat(now, '_yyyy-mm-dd_HH-MM-ss')
    return `${prefix}${formatid.value}${dateString}.${ext}`
  }

  function getPrefName (): string {
    const city = City as any
    for (const key in city.prefs) {
      if (basicData.value.address1 && basicData.value.address1.indexOf(city.prefs[key]) >= 0) {
        return city.prefs[key]
      }
    }
    return ''
  }

  function getCityName (): string {
    const city = City as any
    for (const key in city.cities) {
      if (basicData.value.address1 && basicData.value.address1.indexOf(city.cities[key]) >= 0) {
        return city.cities[key]
      }
    }
    return ''
  }

  // ---- ダイアログを開いている間だけ send-* を実行するためのガード ----
  /**
   * 5 ダイアログが同じ send-* イベントに対し listener を持つことを想定したガード。
   * 自モーダルが visible 中の場合のみ handler を実行する。
   */
  function whenModalActive<T extends (...args: never[]) => unknown> (handler: T): T {
    return ((...args: never[]) => {
      if (!modalVisible.value) return
      return handler(...args)
    }) as T
  }

  // ---- rtSendFactory: send-* 関数群を全取得 ----
  const rtSendFactory = inject(RtSendFactoryKey, useRealtimeTransferSend)
  const sendHandlers = rtSendFactory({
    vm,
    isAgree,
    isRealtimeTransferCommonSended: isSended,
    fullscreenLoading,
    pdfFormData,
    formatid,
    username,
    clientId,
    dialogName,
    permissionType,
    basicData,
    dialogInfo,
    isMypage,
    createFilename,
    getPrefName,
    getCityName,
    bizreachPassword,
    bizreachPasswordFormRef
  })

  // ---- open ----
  async function openDialog (
    id: string,
    html: any,
    perm: number,
    info: Record<string, any>,
    bd: Record<string, any>,
    mypage = false,
    ...extraArgs: unknown[]
  ) {
    isMypage.value = mypage
    formatid.value = id
    previewHtml.value = html && html.$el ? html.$el.innerHTML : ''
    basicData.value = bd || {}

    if (applyExtraArgs && extraArgs.length > 0) {
      applyExtraArgs(extraArgs)
    }

    if (beforeApplyInfo) {
      const result = await beforeApplyInfo({ perm, info })
      if (result === false) return
      if (result && typeof result === 'object') {
        perm = result.perm ?? perm
        info = result.info ?? info
      }
    }

    permissionType.value = perm === 0 && defaultPermissionType ? defaultPermissionType : perm
    clientId.value = info.clientId ?? defaultClientId
    dialogName.value = info.dialogName ?? defaultDialogName
    dialogInfo.value = info

    // isAgree の挙動は各ダイアログで微妙に異なる（Common: requiredAgree 時のみ false、
    // Bizreach: 常に !requiredAgree、Medley/MedleyLink: 触らない/常に true）ため、
    // base は触らず resetStateBeforeOpen / afterApplyInfo で各ダイアログが制御する。
    if (resetStateBeforeOpen) resetStateBeforeOpen()
    if (afterApplyInfo) afterApplyInfo({ info })

    // displayDialog 計測 POST（5 ダイアログ共通）
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatid.value)
    formData.append('dialog_name', dialogName.value)
    formData.append('permission_type', String(permissionType.value))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('is_mypage', isMypage.value ? 'true' : 'false')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    vm.$axios.post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 })

    vm.$iterateStorage((value: any, key: string) => {
      if (key === 'basicData') username.value = `${value.lastName}_${value.firstName}`
    }, (err: any) => { if (!err) console.log('Iteration has completed') })

    isSended.value = false

    if (modalOpened.value) {
      pendingReshow.value = true
      replacingOpen.value = true
      modalVisible.value = false
    } else {
      nextTick(() => { modalVisible.value = true })
    }
  }

  function onModalOpened () {
    modalOpened.value = true
  }

  function onModalClosed () {
    modalOpened.value = false
    if (pendingReshow.value) {
      pendingReshow.value = false
      replacingOpen.value = false
      nextTick(() => { modalVisible.value = true })
    }
  }

  function closeDialog () {
    const conditionId = dialogInfo.value && dialogInfo.value.conditionId
    // update_rt_condition: 5 引数で統一（受け側 FormatEdit/MyPage*/MyPageRirekishoEdit は
    // arguments[4] で conditionId を参照しており、後方互換あり）
    vm.$eventHub.$emit(
      'update_rt_condition',
      clientId.value,
      permissionType.value,
      isSended.value,
      dialogName.value,
      conditionId
    )
    modalVisible.value = false
  }

  function handleBeforeClose () {
    if (replacingOpen.value) return
    if (!isSended.value) {
      emitRtDialogClosedWithoutSubmit()
    }
    if (onBeforeCloseExtra) {
      onBeforeCloseExtra({ isSended: isSended.value, isMypage: isMypage.value })
    }
  }

  const showOtherJobButton = computed(() => isSended.value)
  const rtOtherJobButtonLabel = RT_SUB_ACCEPT_LABEL_AFTER_SEND

  function onRtOtherJobBtnClick () {
    if (!isSended.value) return
    closeDialog()
    emitRtChainAdvanceRequest('request-other-job')
  }

  // ---- IndexedDB から username を初期ロード（5 ダイアログ共通） ----
  vm.$iterateStorage((value: any, key: string) => {
    if (key === 'basicData') username.value = `${value.lastName}_${value.firstName}`
  }, (err: any) => { if (!err) console.log('Iteration has completed') })

  // ---- mitt / eventHub の登録 ----
  let mittGVPopupHandler: ((argList: unknown) => void) | undefined
  const consultEmitterHandlers: Array<{ event: string; handler: (...args: unknown[]) => unknown }> = []

  /**
   * send-* イベントを「自モーダルがアクティブな時だけ実行」ガードして登録するヘルパ。
   * Common/Test1 が RT 共通群を追加で登録する用途と、base 内部での primary 登録の両方に使う。
   */
  const registerConsultEmitter: RegisterConsultEmitter = (event, handler) => {
    const guarded = whenModalActive(handler)
    consultEmitterHandlers.push({ event, handler: guarded })
    vm.$emitter.on(event, guarded)
  }

  onBeforeMount(() => {
    mittGVPopupHandler = (argList: unknown) => {
      if (Array.isArray(argList)) {
        openDialog(...(argList as Parameters<typeof openDialog>))
      }
    }
    vm.$emitter.on(openDialogEventName, mittGVPopupHandler)

    if (primaryConsultEventName && primaryConsultHandlerKey) {
      const handler = sendHandlers[primaryConsultHandlerKey] as (...args: unknown[]) => unknown
      if (typeof handler === 'function') {
        registerConsultEmitter(primaryConsultEventName, handler)
      }
    }

    vm.$eventHub.$on('popstate', closeDialog)
  })

  onUnmounted(() => {
    vm.$emitter.off(openDialogEventName, mittGVPopupHandler)
    for (const { event, handler } of consultEmitterHandlers) {
      vm.$emitter.off(event, handler)
    }
    vm.$eventHub.$off('popstate', closeDialog)
  })

  return {
    // ---- state ----
    isMypage,
    formatid,
    previewHtml,
    permissionType,
    isSended,
    fullscreenLoading,
    username,
    clientId,
    dialogName,
    isAgree,
    basicData,
    dialogInfo,
    modalVisible,
    modalOpened,
    pendingReshow,
    replacingOpen,

    // ---- computed ----
    pdfFormData,

    // ---- 共通関数 ----
    createFilename,
    getPrefName,
    getCityName,

    // ---- ダイアログ操作 ----
    openDialog,
    closeDialog,
    handleBeforeClose,
    onModalOpened,
    onModalClosed,
    showOtherJobButton,
    rtOtherJobButtonLabel,
    onRtOtherJobBtnClick,

    // ---- ガード ----
    whenModalActive,

    // ---- 送客関数（全部・Bizreach/Medley/MedleyLink 含む） ----
    ...sendHandlers,

    // ---- 拡張 API（useRtCommonDialog 等が追加 listener を登録するため） ----
    vm,
    registerConsultEmitter
  }
}

export type UseRtDialogBaseReturn = ReturnType<typeof useRtDialogBase>

/**
 * RT 共通の送客 hub イベント群（send-recurrent-consult / send-xmile-consult / ...）を
 * base に一括追加登録するヘルパ。
 *
 * 用途: RealtimeTransferCommonDialog / RealtimeTransferAppealDialog のように
 *       「ハブとして任意クライアントの送客 API を受けたい」ダイアログから呼ぶ。
 *
 * 各 send-* は base.registerConsultEmitter 経由で modalVisible ガード付きで登録されるため、
 * 複数ダイアログ間で同じイベントを listen していても自モーダルが active な時のみ発火する。
 *
 * 新クライアント追加時はこの 1 箇所に行を追加すれば、ハブ全 dialogs に反映される。
 */
export function registerAllRtConsultEmitters (base: UseRtDialogBaseReturn): void {
  const { registerConsultEmitter } = base
  registerConsultEmitter('send-recurrent-consult', base.sendRecurrentConsult)
  registerConsultEmitter('send-xmile-consult', base.sendXMileConsult)
  registerConsultEmitter('send-tryt-api-consult', base.sendTrytApiConsult)
  registerConsultEmitter('send-workport-consult', base.sendWorkportConsult)
  registerConsultEmitter('send-hrcareer-consult', base.sendHrcareerConsult)
  registerConsultEmitter('send-recree-consult', base.sendRecreeConsult)
  registerConsultEmitter('send-careerdraft-consult', base.sendCareerdraftConsult)
  registerConsultEmitter('send-rozas-consult', base.sendRozasConsult)
  registerConsultEmitter('send-neocareer-consult', base.sendNeocareerConsult)
  registerConsultEmitter('send-recruit_mc-consult', base.sendRecruitMcConsult)
  registerConsultEmitter('send-adtop-consult', base.sendAdtopConsult)
  registerConsultEmitter('send-agrinavi-consult', base.sendAgrinaviConsult)
  registerConsultEmitter('send-asiro-consult', base.sendAsiroConsult)
  registerConsultEmitter('send-baton-consult', base.sendBatonConsult)
  registerConsultEmitter('send-bridge-consult', base.sendBridgeConsult)
  registerConsultEmitter('send-capla-consult', base.sendCaplaConsult)
  registerConsultEmitter('send-dely-consult', base.sendDelyConsult)
  registerConsultEmitter('send-growtrail-consult', base.sendGrowtrailConsult)
  registerConsultEmitter('send-jobs919-consult', base.sendJobs919Consult)
  registerConsultEmitter('send-hitotech-consult', base.sendHitotechConsult)
  registerConsultEmitter('send-hitotechneocareer-consult', base.sendHitotechneocareerConsult)
  registerConsultEmitter('send-jaic-consult', base.sendJaicConsult)
  registerConsultEmitter('send-kirara-consult', base.sendKiraraConsult)
  registerConsultEmitter('send-krasys-consult', base.sendKrasysConsult)
  registerConsultEmitter('send-miraiyu-consult', base.sendMiraiyuConsult)
  registerConsultEmitter('send-mynavibaite-consult', base.sendMynaviBaiteConsult)
  registerConsultEmitter('send-mynavims-consult', base.sendMynaviMsConsult)
  registerConsultEmitter('send-peacekaigo-consult', base.sendPeacekaigoConsult)
  registerConsultEmitter('send-persolinnovation-consult', base.sendPersolinnovationConsult)
  registerConsultEmitter('send-pleiades-consult', base.sendPleiadesConsult)
  registerConsultEmitter('send-reastage-consult', base.sendReastageConsult)
  registerConsultEmitter('send-simple-consult', base.sendSimpleConsult)
  registerConsultEmitter('send-systemshared-consult', base.sendSystemsharedConsult)
  registerConsultEmitter('send-techouse-consult', base.sendTechouseConsult)
  registerConsultEmitter('send-tunaguba-consult', base.sendTunagubaConsult)
  registerConsultEmitter('send-usenworking-consult', base.sendUsenworkingConsult)
  registerConsultEmitter('send-yagish-consult', base.sendYagishConsult)
  registerConsultEmitter('send-new-rt-system-consult', base.sendNewRtSystemConsult)
  registerConsultEmitter('send-yagishcareer-consult', base.sendYagishCareerConsult)
  registerConsultEmitter('send-realive-consult', base.sendRealiveConsult)
  registerConsultEmitter('send-bizreach-consult', base.sendBizreachConsult)
}
